export default (
  wire,
  valueOrConfig,
  options = {},
  hasOptGroups = false,
  hasSortedOptions = false,
  isSearchable = false,
  searchesLive = false,
  isMultiple = false,
  showEmptyValue = false,
  displayLimit = 0,
  disabledOptions = []
) => ({
  query: '',
  timeout: null,
  loading: false,
  _subKey: (valueOrConfig && typeof valueOrConfig === 'object' && !valueOrConfig._x_interceptor && valueOrConfig.subKey) ? valueOrConfig.subKey : null,
  _entangled: (valueOrConfig && typeof valueOrConfig === 'object' && !valueOrConfig._x_interceptor && valueOrConfig.subKey) ? valueOrConfig.value : null,
  selected: (() => {
    const hasSubKey = valueOrConfig && typeof valueOrConfig === 'object' && !valueOrConfig._x_interceptor && valueOrConfig.subKey;
    if (!hasSubKey) return valueOrConfig;
    const entangled = valueOrConfig.value;
    const sk = valueOrConfig.subKey;
    const raw = Alpine.raw(entangled);
    if (raw && typeof raw === 'object' && raw._x_interceptor) {
      const resolved = ('live' in raw) ? raw.live : raw.initialValue;
      return resolved?.[sk] ?? (isMultiple ? [] : '');
    }
    return raw?.[sk] ?? (isMultiple ? [] : '');
  })(),
  selectedElements: [],
  elements: options ?? wire.options,
  open: false,
  dropdownTop: 0,
  dropdownLeft: 0,
  dropdownWidth: 0,
  activeIndex: 0,

  activeIndexSelectAll: 0,
  activeIndexSelectEmpty: 0,

  handleKeyDownRef: null,
  handleScrollRef: null,
  handleResizeRef: null,
  handleClickRef: null,

  _morphedCleanup: null,
  _pageHideRef: null,
  _listenersActive: false,

  get elementObjects() {
    if (hasOptGroups) {
      return Object.entries(this.elements)
        .flatMap(([group, items]) => {
          const keys = Object.keys(items);
          return keys.map(key => ({
            id: key,
            name: items[key],
            group,
          }));
        });
    }

    return Object.keys(this.elements)
      .map(key => ({
        id: key,
        name: this.elements[key],
      }));
  },

  get filteredElements() {
    if (searchesLive) {
      return this.elementObjects;
    }

    return this.query === ''
      ? this.elementObjects
      : this.elementObjects.filter(element => {
        return element.name.toLowerCase().includes(this.query.toLowerCase())
      })
  },

  get sortedFilteredElements() {
    const sort = elements => {
      if (!hasSortedOptions) {
        return elements;
      }

      const hasNumericNames = elements.length > 0 && elements.every(element => {
        const value = element.name;

        if (value === null || value === undefined || value === '') {
          return false;
        }

        return Number.isFinite(Number(value));
      });

      if (hasNumericNames) {
        return elements;
      }

      return elements.sort((a, b) =>
        String(a.name ?? '').localeCompare(String(b.name ?? ''))
      );
    };
    const elements = [...this.filteredElements];

    if (!hasOptGroups) {
      return sort(elements);
    }

    const groups = new Map();
    elements.forEach(element => {
      if (!groups.has(element.group)) groups.set(element.group, []);
      groups.get(element.group).push(element);
    });

    return [...groups].flatMap(([group, items]) =>
      sort(items).map((element, index) => ({ ...element, group: index === 0 ? group : null }))
    );
  },

  get flatElements() {
    if (! hasOptGroups) return this.elements;

    return Object.values(this.elements).reduce((acc, obj) => {
      return { ...acc, ...obj };
    }, {});
  },

  get selectedValue() {
    const value = this.selected;

    if (value && typeof value === 'object' && value._x_interceptor) {
      return ('live' in value) ? value.live : value.initialValue;
    }

    return Alpine.raw(value);
  },

  get selectedArray() {
    const raw = Alpine.raw(this.selected);

    if (Array.isArray(raw)) return raw;

    return raw !== null && raw !== undefined && raw !== '' ? [raw] : [];
  },

  toggleSelectAll(userInitiated = false) {
    const before = this.selectionSnapshot();
    const enabled = this.elementObjects
      .filter(element => !this.isOptionDisabled(element.id))
      .map(element => element.id);
    const disabledSelected = this.selectedArray.filter(value => this.isOptionDisabled(value));

    this.selected = this.isSelectedAll
      ? disabledSelected
      : [...disabledSelected, ...enabled];

    this.dispatchSelectionChange(before, userInitiated);
  },

  get isSelectedAll() {
    const enabled = this.elementObjects.filter(element => !this.isOptionDisabled(element.id));

    return enabled.length > 0
      && enabled.every(element => this.selectedArray.some(item => item == element.id));
  },

  isOptionDisabled(value) {
    return disabledOptions.some(option => option == value);
  },

  selectionSnapshot() {
    return isMultiple ? [...this.selectedArray] : this.selectedValue;
  },

  selectionEquals(left, right) {
    if (!isMultiple) return left == right;
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;

    return left.every((value, index) => value == right[index]);
  },

  dispatchSelectionChange(before, userInitiated) {
    if (userInitiated !== true || this.selectionEquals(before, this.selectionSnapshot())) return;

    this.$root?.dispatchEvent(new Event('change', { bubbles: true }));
  },

  toggleElement(value, userInitiated = false) {
    if (this.isOptionDisabled(value)) return;

    const before = this.selectionSnapshot();

    if (isMultiple) {
      const selected = this.selectedArray;

      if (selected.some(item => item == value)) {
        this.selected = selected.filter(item => item != value);
      } else {
        // Dedupe on add: guards against pre-existing inconsistent state (e.g. ["a", "a"])
        // propagating further. Uses == to stay consistent with isSelected()/remove path.
        const seen = [];
        for (const item of selected) {
          if (!seen.some(s => s == item)) {
            seen.push(item);
          }
        }
        this.selected = [...seen, value];
      }
    }
    else {
      this.selected = value;
      this.closeDropdown();
    }

    this.dispatchSelectionChange(before, userInitiated);
  },

  isSelected(value) {
    // comparison with == to handle also 1 = "1"
    return isMultiple
        ? this.selectedArray.some(item => item == value)
        : this.selectedValue == value;
  },

  get displayValue() {
    if (this.selectedElements.length == 0) {
      return '—'
    }

    let showCount = displayLimit > 0
      ? Math.min(displayLimit, this.selectedElements.length)
      : this.selectedElements.length

    return this.selectedElements.slice(0, showCount)
     .map(element => element.name)
     .join(', ') + (showCount < this.selectedElements.length ? `, + ${this.selectedElements.length - showCount}` : '')
  },

  init() {
    this.handleClickRef = this.handleClick.bind(this);
    this.handleKeyDownRef = this.handleKeyDown.bind(this);
    this.handleScrollRef = this.updateDropdownPosition.bind(this);
    this.handleResizeRef = this.updateDropdownPosition.bind(this);

    // Livewire morph hook: when our root is gone or got morphed, tear down listeners / close dropdown
    if (window.Livewire?.hook) {
      this._morphedCleanup = window.Livewire.hook('morphed', (component) => {
        if (!this.$root || !this.$root.isConnected) {
          this.cleanupEventListeners();
          this.open = false;
          return;
        }
        if (
          component?.el &&
          (
            component.el === this.$root ||
            component.el.contains(this.$root) ||
            this.$root.contains(component.el)
          )
        ) {
          if (this.open) {
            this.closeDropdown();
          }
        }
      });
    }

    // bfcache / page hide: make sure no zombie listeners survive
    this._pageHideRef = () => this.cleanupEventListeners();
    window.addEventListener('pagehide', this._pageHideRef);

    // Livewire navigate (back/forward + bfcache): the teleported dropdown tree
    // may survive as a stale clone in <body> while Alpine re-initialises the
    // new page. Force-close + purge the dropdown content so initTree cannot
    // evaluate x-for clones against a dead `element` scope.
    this._navigateRef = () => {
      this.open = false;
      this.cleanupEventListeners();
      const dd = this.$refs?.dropdown;
      if (dd && dd.isConnected && dd.parentNode === document.body) {
        try { dd.parentNode.removeChild(dd); } catch (e) {}
      }
    };
    window.addEventListener('livewire:navigate', this._navigateRef);
    window.addEventListener('livewire:navigating', this._navigateRef);

    if (showEmptyValue) {
      this.activeIndexSelectEmpty = 1;
    }

    if (!searchesLive && isMultiple) {
      this.activeIndexSelectAll = 1;
    }

    if (isMultiple) {
      const selected = this.selectedArray;

      if (selected.length) {
        this.selectedElements = selected.map((value) => ({
          id: value,
          name: this.flatElements[value],
        }));
      }
    }
    else {
      const selected = this.selectedValue;

      if (selected !== null && selected !== undefined && selected !== '') {
        this.selectedElements = [
          {
            id: selected,
            name: this.flatElements[selected],
          }
        ];

        this.$nextTick(() => {
          // Cleanup if necessary
          if (this.flatElements[selected] === undefined) {
            this.toggleElement(null);
          }
        })
      }
    }

    if (searchesLive) {
      this.$watch('query', (newquery, oldquery) => {
        if (newquery.length === 0) {
          // just commented out for test - was causing not load, if emptied search field but might cause other problems?!
          //return;
        }

        clearTimeout(this.timeout);
        this.timeout = setTimeout(async () => {
          const currentQuery = this.query;
          this.loading = true;
          try {
            const result = await wire.updateOptions(currentQuery);
            // Drop out-of-order / stale responses: if the query moved on while
            // this request was in flight, ignore its result instead of letting
            // an older payload overwrite the newer one.
            if (currentQuery !== this.query) {
              return;
            }
            this.elements = result;
          } catch (e) {
            // Swallow transport errors – the UI should not stay stuck in a
            // loading state just because a request failed. The error is
            // surfaced by Livewire itself.
          } finally {
            // Always release the busy flag – otherwise a failed or stale
            // request would leave the UI stuck in a loading state.
            if (currentQuery === this.query) {
              this.loading = false;
            }
          }
        }, 300);
      });
    }

    this.$watch('selected', (newselected, oldselected) => {
      newselected = Alpine.raw(newselected);

      // Sync back to entangled parent object when subKey is used
      if (this._subKey && this._entangled) {
        const parent = Alpine.raw(this._entangled);
        const current = (parent && typeof parent === 'object' && parent._x_interceptor)
          ? { ...(parent.initialValue ?? parent.live ?? {}) }
          : { ...(parent ?? {}) };
        current[this._subKey] = newselected;
        this._entangled = current;
      }

      // normalize to an array
      let ids = Array.isArray(newselected)
        ? newselected
        : newselected !== null && newselected !== undefined && newselected !== ''
          ? [newselected]
          : [];

      // rebuild selectedElements every time
      this.selectedElements = ids.map(id => ({
        id,
        name: this.flatElements[id],
      }));

      const checkbox = this.$el
        .closest('.form-element')
        ?.querySelector('label input[type=checkbox]:not([data-field-activation-mode=free])')

      if (checkbox && !checkbox.checked) {
        checkbox.click();
      }
    });
  },

  destroy() {
    this.cleanupEventListeners();

    if (this._morphedCleanup) {
      try { this._morphedCleanup(); } catch (e) {}
      this._morphedCleanup = null;
    }

    if (this._pageHideRef) {
      window.removeEventListener('pagehide', this._pageHideRef);
      this._pageHideRef = null;
    }

    if (this._navigateRef) {
      window.removeEventListener('livewire:navigate', this._navigateRef);
      window.removeEventListener('livewire:navigating', this._navigateRef);
      this._navigateRef = null;
    }
  },

  registerEventListeners() {
    if (this._listenersActive) return;
    window.addEventListener('scroll', this.handleScrollRef, true);
    window.addEventListener('resize', this.handleResizeRef);
    document.addEventListener('click', this.handleClickRef);
    this._listenersActive = true;
  },

  cleanupEventListeners() {
    if (!this._listenersActive) return;
    window.removeEventListener('scroll', this.handleScrollRef, true);
    window.removeEventListener('resize', this.handleResizeRef);
    document.removeEventListener('click', this.handleClickRef);
    // keydown hangs on the dropdown element; it might already be gone -> optional chaining
    this.$refs?.dropdown?.removeEventListener?.('keydown', this.handleKeyDownRef);
    this._listenersActive = false;
  },

  updateQuery(value) {
    this.query = value ?? ''
  },

  openDropdown(initialChar = '') {
    this.open = true
    this.query = initialChar;
    this.activeIndex = this.activeIndices[0] ?? 0;

    // Add event listeners to synchronize the dropdown
    this.registerEventListeners();

    this.$nextTick(() => {
      this.updateDropdownPosition();
      requestAnimationFrame(() => this.updateDropdownPosition());

      this.$refs.dropdown?.addEventListener('keydown', this.handleKeyDownRef);
      if (isSearchable) {
        this.$refs.input?.focus();
      } else {
        this.$refs.dropdown?.focus();
      }
    });
  },

  closeDropdown() {
    this.open = false
    this.activeIndex = null;

    // Remove event listeners when dropdown is closed
    this.cleanupEventListeners();

    this.$nextTick(() => {
      this.$refs.triggerButton?.focus();
    });
  },

  scrollToActiveElement() {
    const dropdown = this.$refs?.dropdown;

    if (!dropdown || !dropdown.isConnected) {
      return;
    }

    const activeItem = dropdown.querySelector(`[data-index='${this.activeIndex}']`);

    if (activeItem) {
      activeItem.scrollIntoView({
        behavior: 'smooth', // Optional: Adds smooth scrolling
        block: 'nearest',  // Ensures minimal scrolling
      });
    }
  },

  scrollToInput() {
    const input = this.$refs.input;

    if (input) {
      input.scrollIntoView({
        behavior: 'smooth', // Optional: Adds smooth scrolling
        block: 'nearest',  // Ensures minimal scrolling
      });
    }
  },

  get activeIndices() {
    const offset = this.activeIndexSelectAll + this.activeIndexSelectEmpty;
    const indices = Array.from({ length: offset }, (_, index) => index);

    this.sortedFilteredElements.forEach((element, index) => {
      if (!this.isOptionDisabled(element.id)) {
        indices.push(index + offset);
      }
    });

    return indices;
  },

  moveActiveIndex(direction) {
    if (!this.open) return;

    const indices = this.activeIndices;
    if (indices.length === 0) {
      this.activeIndex = null;
      return;
    }

    const current = indices.indexOf(this.activeIndex);
    if (direction === 'up') {
      this.activeIndex = current <= 0 ? indices[indices.length - 1] : indices[current - 1];
    } else if (direction === 'down') {
      this.activeIndex = current < 0 || current >= indices.length - 1 ? indices[0] : indices[current + 1];
    }

    if (isSearchable && this.activeIndex === 0) {
      this.scrollToInput();
    }
    else {
      this.scrollToActiveElement();
    }
  },

  selectActiveElement() {
    if (this.activeIndex === null) return;

    const dropdown = this.$refs?.dropdown;

    if (!dropdown || !dropdown.isConnected) {
      return;
    }

    const activeItem = dropdown.querySelector(`[data-index='${this.activeIndex}']`);

    if (activeItem) {
      activeItem.click();
    }
  },

  handleKeyDown(event) {
    const navKey = ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key);

    if (!this.open) {
      if (navKey) {
        event.preventDefault();
        this.openDropdown();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActiveIndex('down');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActiveIndex('up');
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.selectActiveElement();
    } else if (event.key === 'Tab') {
      this.closeDropdown();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    }
  },

  handleTriggerKeyDown(event) {
    const navKey = ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key);
    const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    } else if (navKey) {
      event.preventDefault();
      this.openDropdown();
    } else if (printable) {
      event.preventDefault();
      this.openDropdown(event.key);
    }
  },

  updateDropdownPosition() {
    if (!this.open) return;

    const container = this.$refs?.container;
    const dropdown = this.$refs?.dropdown;

    if (!container || !dropdown || !container.isConnected || !dropdown.isConnected) {
      this.cleanupEventListeners();
      this.open = false;
      return;
    }

    const inputRect = container.getBoundingClientRect();
    const modalContainer = container.closest('#modal-container')
      || container.closest('.dialog');

    const bounds = modalContainer
      ? modalContainer.getBoundingClientRect()
      : {
          top: 0,
          bottom: window.innerHeight,
          left: 0,
          right: window.innerWidth,
        };

    const outOfView = inputRect.bottom <= bounds.top || inputRect.top >= bounds.bottom;
    if (outOfView) {
      this.closeDropdown();
      return;
    }

    const availableBelow = bounds.bottom - inputRect.bottom;
    const availableAbove = inputRect.top - bounds.top;
    const estimatedHeight = dropdown?.offsetHeight || 220;
    const openUpwards = availableBelow < estimatedHeight && availableAbove > availableBelow;

    // Teleported overlays use fixed positioning, whether their target is body or
    // a native dialog's local top-layer portal. Coordinates therefore stay in
    // viewport space; modal/dialog bounds only decide the opening direction and
    // whether scrolling moved the trigger out of its container.
    this.dropdownLeft = inputRect.left;
    this.dropdownWidth = inputRect.width;

    if (openUpwards) {
      this.dropdownTop = inputRect.top - estimatedHeight;
      return;
    }

    this.dropdownTop = inputRect.bottom;
  },

  handleClick(event) {
    if (!this.open) return;

    const container = this.$refs?.container;
    const dropdown = this.$refs?.dropdown;

    // Refs missing / detached from DOM -> zombie listener, bail out and cleanup.
    if (!container || !dropdown || !container.isConnected || !dropdown.isConnected) {
      this.cleanupEventListeners();
      this.open = false;
      return;
    }

    const target = event.target;
    if (!container.contains(target) && !dropdown.contains(target)) {
      this.closeDropdown();
    }
  }
})
